// WHO ELSE IS AT THE COMPANY — the people a lead card's recipient dropdown
// offers, and the identity of the one it is showing right now.
//
// WHY THIS EXISTS. A run writes each initial-outreach email to ONE address:
// the row's primary contact (the loader's "first person with an email, else
// the first person" — load_masonry_leads.py#primary_contact), or the first
// address the engine could recover from the packed contacts blob. Bo often
// knows better than the row: the estimator, not the CFO; the owner, not the
// office manager. The card therefore lets him switch to another person at the
// company and has the email rewritten for them on the spot (lib/runHost.js#
// switchLeadRecipient). This module is the pure half of that: it reads the
// people off the row, works out which of them the card is addressed to, and
// gives every person a stable key so a draft written for them can be found
// again.
//
// TWO SOURCES FOR THE PEOPLE, read in this order:
//   1. the row's packed `contacts` blob — "name=…; apollo_title=…;
//      apollo_email=…" groups (lib/leadSearch.js#parsePackedContacts), which is
//      every person the workbook listed for the company, most of them WITHOUT
//      an address (the workbooks carry a title and a phone far more often
//      than an email);
//   2. the row's primary-contact columns (contact_name / contact_role / email)
//      and the stub's own contact_name — so a lead whose people were never
//      packed (a CRM-only row, a manual lead, an old receipt with no row in
//      the mirror) still offers the one person it knows.
//
// A PERSON WITHOUT AN ADDRESS IS STILL A PERSON. Most of the people on a real
// row have no email on file, and leaving them out would make the dropdown a
// list of one on most cards. They are offered, marked, and when chosen the
// email is written TO THEM BY NAME with no address attached — the card says so
// and Bo finds the address in HubSpot or on LinkedIn (the blob usually carries
// the profile). What is never done is to greet one person at another person's
// mailbox: a choice with no email sends no `recipient` and no `email` to the
// writer, so the draft cannot be addressed to the wrong inbox by accident.
//
// Nothing in here fetches or stores. The one async helper at the bottom reads
// the browser's own encrypted leads mirror, the same way the lead page opens a
// bare /leads/company/<key> URL (pages/LeadDetail.jsx), and caches the
// resulting index per mirror generation.
import { parsePackedContacts } from './leadSearch.js'
import { keyOf } from './companyMeta.js'

const clean = (v) => String(v ?? '').replace(/\s+/g, ' ').trim()

// Role values the workbooks use as "we could not verify a title". Not a role;
// rendered as no role at all rather than as a word beside the name.
const NO_ROLE = /^(unverified|unknown|n\/?a|none|-+|\?+)$/i

/** A person's title as the dropdown shows it — '' for a placeholder. */
export function roleOf(raw) {
  const t = clean(raw)
  return NO_ROLE.test(t) ? '' : t
}

// A name, compared the way the loader dedupes people: case-, spacing- and
// apostrophe-blind ("Dylan  O’Meara" == "dylan o'meara").
const nameKey = (name) => clean(name).toLowerCase().replace(/[’']/g, '')

/**
 * THE IDENTITY OF A RECIPIENT: their address when they have one, their name
 * otherwise, prefixed so the two spaces can never collide. This is the key a
 * draft written for a person is cached under and the key a stale answer is
 * checked against (lib/chat.js#patchReceiptLead `forRecipient`), so it must be
 * a pure function of the person and nothing else. '' for nobody.
 */
export function recipientKey(person = {}) {
  const email = clean(person?.email).toLowerCase()
  if (email.includes('@')) return email
  const name = nameKey(person?.name)
  return name ? `name:${name}` : ''
}

/**
 * WHO THE RUN WROTE TO — the default recipient, read off the stub exactly the
 * way the resolver reads it: the stub's address, and the person its stored
 * `greeting` names (lib/greeting.js decided that when the receipt was packed;
 * here it is only the label). A row, when one is given, fills in the role and,
 * when the address's packed owner is the person greeted, their profile.
 *
 * @returns {{ name: string, title: string, email: string, key: string, linkedin: string }}
 */
export function defaultRecipientOf(lead = {}, row = null) {
  const email = clean(lead?.email || row?.email).toLowerCase()
  // THE PERSON THE EMAIL SAYS HELLO TO, as the run worked it out from the
  // full row (chat.js#leadStub -> lead.greeting, lib/greeting.js). The card
  // names the same person the email greets: an address that is plainly
  // somebody else's (beth@ on a row naming Burke Roble) shows "Beth", not the
  // row's contact. A stub from before greetings were stored keeps the old
  // reading below.
  const greeting = lead?.greeting && typeof lead.greeting === 'object' ? lead.greeting : null
  let name = greeting ? clean(greeting.person || greeting.name) : clean(lead?.contact_name || row?.contact_name)
  let title = roleOf(row?.contact_role)
  let linkedin = ''
  if (email) {
    const match = peopleOf(row).find((p) => p.email === email)
    // The packed person behind the address lends their spelling, title and
    // profile only when they ARE the person greeted.
    if (match && (!greeting || greeting.how === 'owner')) {
      if (!greeting) name = match.name || name
      title = match.title || title
      linkedin = match.linkedin
    }
  }
  return { name, title, email, linkedin, key: recipientKey({ name, email }) }
}

/**
 * THE RECIPIENT A CARD IS SHOWING: the rep's pick when the stub carries one
 * (`lead.recipient`, written by switchLeadRecipient), the run's default
 * otherwise. The `picked` flag is what the card keys its Copied mark and its
 * story on — a picked person is a different draft from the run's.
 */
export function currentRecipientOf(lead = {}, row = null) {
  const picked = lead?.recipient && typeof lead.recipient === 'object' ? lead.recipient : null
  if (picked) {
    const name = clean(picked.name)
    const title = roleOf(picked.title)
    const email = clean(picked.email).toLowerCase()
    const key = recipientKey({ name, email })
    if (key) {
      return { name, title, email, linkedin: clean(picked.linkedin), key, picked: true }
    }
  }
  return { ...defaultRecipientOf(lead, row), picked: false }
}

/** Every person packed on a row, normalised and deduped by identity. */
function peopleOf(row) {
  if (!row || typeof row !== 'object') return []
  const seen = new Set()
  const out = []
  for (const c of parsePackedContacts(row.contacts)) {
    const name = clean(c.name)
    if (!name) continue
    const email = clean(c.email).toLowerCase()
    const key = recipientKey({ name, email: email.includes('@') ? email : '' })
    if (!key || seen.has(key)) continue
    seen.add(key)
    out.push({
      key,
      name,
      title: roleOf(c.title),
      email: email.includes('@') ? email : '',
      linkedin: clean(c.linkedin),
    })
  }
  return out
}

/**
 * THE DROPDOWN'S ROWS. Every person on the row, in the row's own order, with
 * the card's current recipient guaranteed to be among them (inserted first
 * when the row does not list them — a recovered address, a CRM-only lead, an
 * old receipt) and the run's default marked so the card can tell "back to who
 * the run picked" from "somebody new".
 *
 * `row` may be the mirror row, the intel snapshot, or null; `lead` is the
 * receipt stub. Pure, and cheap enough to run per render.
 *
 * @returns {{
 *   choices: Array<{ key, name, title, email, linkedin, isDefault: boolean }>,
 *   current: { key, name, title, email, linkedin, picked: boolean },
 *   defaultKey: string,
 * }}
 */
export function contactChoices(row = null, lead = {}) {
  const people = peopleOf(row)
  const fallback = defaultRecipientOf(lead, row)
  const current = currentRecipientOf(lead, row)
  const defaultKey = fallback.key
  const choices = people.map((p) => ({ ...p, isDefault: p.key === defaultKey }))
  // The run's own recipient, when the packed people do not include them: the
  // row's address came from outside the blob, or the row is not in the mirror.
  if (defaultKey && !choices.some((c) => c.key === defaultKey)) {
    choices.unshift({
      key: defaultKey,
      name: fallback.name,
      title: fallback.title,
      email: fallback.email,
      linkedin: fallback.linkedin,
      isDefault: true,
    })
  }
  // The rep's pick, when it is no longer on the row (the mirror re-synced and
  // the person is gone): still shown, still selected — the card is showing
  // their email and must be able to say whose it is.
  if (current.key && !choices.some((c) => c.key === current.key)) {
    choices.unshift({
      key: current.key,
      name: current.name,
      title: current.title,
      email: current.email,
      linkedin: current.linkedin,
      isDefault: current.key === defaultKey,
    })
  }
  return { choices, current, defaultKey }
}

/** "Ben Hester · President" — the one-line form of a person. */
export function personLabel(person = {}) {
  const name = clean(person?.name) || clean(person?.email) || 'Unnamed contact'
  const title = roleOf(person?.title)
  return title ? `${name} · ${title}` : name
}

/**
 * The choice as the stub stores it (`lead.recipient`) and as the draft route
 * is told about it: name, title and address, bounded, nothing else. The key is
 * NOT stored — it is derived, so a spelling fix in recipientKey() can never
 * strand a stored pick.
 */
export function recipientRecordOf(choice = {}) {
  const name = clean(choice?.name).slice(0, 80)
  const title = roleOf(choice?.title).slice(0, 80)
  const email = clean(choice?.email).toLowerCase().slice(0, 160)
  const linkedin = clean(choice?.linkedin).slice(0, 200)
  if (!name && !email.includes('@')) return null
  return {
    name,
    ...(title ? { title } : {}),
    ...(email.includes('@') ? { email } : {}),
    ...(linkedin ? { linkedin } : {}),
  }
}

// ── the mirror index — which row a receipt's lead came from ────────────────
//
// A receipt stub is deliberately small (chat.js#leadStub): no packed people
// ride on it. The people come from the row itself, read out of this browser's
// encrypted leads mirror by the same key the stub carries (keyOf). Built once
// per mirror generation and shared by every card on screen; a category that
// cannot be read is skipped, never guessed.
//
// Loaded lazily and only here, so the pure helpers above stay importable from
// node (the tests) without IndexedDB.
let indexPromise = null
// Generation of the mirror the index was built from, plus whether a manifest
// was in hand: a build made before the manifest arrived may have found an
// empty mirror it could not sync, and must not be served once it can.
let indexGeneration = ''

/**
 * @param {{ manifest?: object, mode?: string, force?: boolean }} [opts]
 *   With a manifest, an EMPTY mirror is synced once before the index is built
 *   (the lead page does the same for a bare URL) — a second device that has
 *   never opened the Leads page otherwise offers nobody on any card.
 * @returns {Promise<Map<string, { row: object, category: string }>>}
 */
export async function leadRowsIndex({ manifest = null, mode = '', force = false } = {}) {
  const [{ getCachedCompanies, syncCategory, getMirrorGeneration }, { CATEGORY_KEYS }, { listManualLeads }] =
    await Promise.all([import('./leadsCache.js'), import('./categories.js'), import('./manualLeads.js')])
  const generation = `${getMirrorGeneration()}:${manifest ? 1 : 0}`
  if (!force && indexPromise && indexGeneration === generation) return indexPromise
  indexGeneration = generation
  indexPromise = (async () => {
    const build = async () => {
      const map = new Map()
      let readable = 0
      for (const cat of CATEGORY_KEYS) {
        try {
          const { rows, stale } = await getCachedCompanies(cat)
          if (stale) continue
          for (const row of rows || []) {
            const k = String(keyOf(row) || '')
            if (k && !map.has(k)) map.set(k, { row, category: cat })
            readable += 1
          }
        } catch {
          /* an unreadable mirror for this category offers nobody — never guesses */
        }
        try {
          for (const row of listManualLeads(cat) || []) {
            const k = String(keyOf(row) || '')
            if (k && !map.has(k)) map.set(k, { row, category: cat })
          }
        } catch {
          /* no manual leads */
        }
      }
      return { map, readable }
    }
    let { map, readable } = await build()
    if (!readable && manifest) {
      for (const cat of CATEGORY_KEYS) {
        try {
          await syncCategory(cat, manifest, mode, () => {})
        } catch {
          /* offline — the cards simply offer what the stub knows */
        }
      }
      ;({ map } = await build())
    }
    return map
  })()
  indexPromise.catch(() => {
    // A failed build must not be served forever: forget it so the next
    // caller tries again.
    if (indexGeneration === generation) {
      indexPromise = null
      indexGeneration = ''
    }
  })
  return indexPromise
}

/** Test seam: forget the built index. */
export function __resetLeadRowsIndex() {
  indexPromise = null
  indexGeneration = ''
}
